import { ReactNode } from 'react';
import { FiCamera, FiDownload } from 'react-icons/fi';

import logoJogaJunto from 'assets/img/logo-jj.png';
import { FOCO_VISIVEL } from 'utils/foco';
import { arquivoDoSoOSelo, desenharSoOSelo } from 'utils/moldura-so-o-selo';

import { ARQUIVO_DO_SELO, ModoDaImagem, molduraDoSelo, SeloDaColecao } from '../selos-modelo';
import BaixarSelo, { ACAO_BAIXAR } from './BaixarSelo';

// D3: raio de 18px do cartão do Meu Perfil v2 não tem degrau no Tailwind. O quadro só vale para o pedido de foto, em
// 4/5 como no design, e mede os textos pela própria largura (container de `inline-size`).
const CAIXA_DO_PEDIDO =
  'relative aspect-[4/5] w-full [container-type:inline-size] overflow-hidden rounded-[18px] bg-brand-blue shadow-jj-selo';
// Mesmo "Baixar" quando não há moldura para baixar: esmaecido, e o clique leva à foto (Meu Perfil v2).
const BAIXAR_SEM_MOLDURA = `${ACAO_BAIXAR} opacity-[0.45]`;

interface CartaoDeSeloProps {
  selo: SeloDaColecao;
  temFoto: boolean;
  modo: ModoDaImagem;
  onAtualizarFoto: () => void;
}

/**
 * Pedido de foto dentro da moldura, como no Meu Perfil v2: a moldura "#Construímos a nossa história" (faixas em chevron,
 * pontos e o título), a caixa branca no lugar do polaroide e o selo no canto. Os tamanhos seguem a largura do cartão
 * (`cqw`). Sem foto, convida a enviar; com foto e sem moldura, a atualizar (AJ2-23, AJ2-25).
 */
function PedidoNaMoldura({ temFoto, arte, onAtualizarFoto }: { temFoto: boolean; arte?: string; onAtualizarFoto: () => void }) {
  return (
    <>
      <span
        aria-hidden="true"
        className="absolute inset-y-0 left-0 w-[22%] bg-brand-purple-light [clip-path:polygon(0_0,55%_0,100%_50%,55%_100%,0_100%)]"
      />
      <span
        aria-hidden="true"
        className="absolute inset-y-0 left-0 w-[16%] bg-brand-yellow [clip-path:polygon(0_0,50%_0,100%_50%,50%_100%,0_100%)]"
      />
      <span
        aria-hidden="true"
        className="absolute inset-y-0 left-0 w-[11%] bg-brand-blue-deep [clip-path:polygon(0_0,45%_0,100%_50%,45%_100%,0_100%)]"
      />
      <span aria-hidden="true" className="absolute right-[7%] top-[7%] aspect-square w-[3.5%] rounded-full bg-brand-yellow" />
      <span
        aria-hidden="true"
        className="absolute right-[5%] top-[44%] aspect-square w-[2.5%] rounded-full bg-brand-purple-light"
      />
      <p
        aria-hidden="true"
        className="absolute left-[18%] right-[6%] top-[5%] m-0 flex items-center gap-[3cqw] font-display font-extrabold uppercase"
      >
        <span className="text-[length:17cqw] leading-[0.8] text-brand-yellow">#</span>
        <span className="text-[length:5.6cqw] leading-[1.05] text-brand-white">
          Construímos
          <br />
          <span className="text-brand-yellow">a nossa história</span>
        </span>
      </p>
      <div className="absolute bottom-[25%] left-[20%] right-[8%] top-[24%] box-border flex flex-col items-center justify-center gap-[3cqw] rounded-lg bg-brand-white p-[5cqw] text-center shadow-jj-03">
        <span
          aria-hidden="true"
          className="flex aspect-square w-[13cqw] shrink-0 items-center justify-center rounded-full bg-surface-100 text-brand-blue"
        >
          <FiCamera className="h-[55%] w-[55%]" />
        </span>
        <p className="m-0 font-display text-[length:6.4cqw] font-bold leading-[1.15] text-brand-blue-dark">
          Compartilhe sua conquista!
        </p>
        <p className="m-0 text-[length:4.8cqw] leading-[1.3] text-content">
          {temFoto ? 'Atualize sua foto de perfil para gerar esta moldura.' : 'Envie uma foto de perfil para usar nesta moldura.'}
        </p>
        <button
          type="button"
          onClick={onAtualizarFoto}
          className={`cursor-pointer self-stretch rounded-[6px] border-0 bg-brand-yellow px-[2cqw] py-[3cqw] font-display text-[length:4.4cqw] font-bold uppercase leading-[1.15] tracking-[0.04em] text-brand-blue-dark hover:bg-button-primary-hover ${FOCO_VISIVEL}`}
        >
          Atualizar foto de perfil
        </button>
      </div>
      {arte && (
        <img src={arte} alt="" className="absolute bottom-[2%] right-[2%] aspect-square w-[27%] object-contain drop-shadow-lg" />
      )}
    </>
  );
}

/**
 * "Só o selo" na tela, montado como no Meu Perfil v2: logo, o selo num disco com anel e a base amarela com categoria,
 * nome, ciclo e #JogaJunto. O PNG baixado continua desenhado no canvas (`desenharSoOSelo`, AJ2-35). A coleção não
 * traz a data da conquista: no lugar de "Conquistado em …", o ciclo, como no PNG.
 *
 * Tudo se mede pela largura do cartão (`cqw`), nas proporções do design (cartão de 436px: logo de 96, disco de 255,
 * arte de 214), para o cartão ficar perto de 4/5 em qualquer coluna. Com disco e logo fixos, a coluna de 269px do
 * Extrato virava um cartão de ~500px de altura. SPEC_DEVIATION: os textos têm piso (11, 16, 12 e 15px), maior que a
 * escala do design numa coluna estreita. Reason: pedido do usuário na homologação ("muito pequenos").
 */
function SoOSeloNoCartao({ selo }: { selo: SeloDaColecao }) {
  return (
    // O disco usa a cor do `button-solid-hover`; o anel, o token `shadow-selo-anel`.
    <div className="relative flex flex-1 flex-col items-center [container-type:inline-size] overflow-hidden rounded-[18px] bg-brand-blue shadow-jj-selo">
      <span aria-hidden="true" className="absolute left-[5%] top-[4%] aspect-square w-[3%] rounded-full bg-brand-yellow" />
      <span aria-hidden="true" className="absolute right-[7%] top-[15%] aspect-square w-[2.2%] rounded-full bg-brand-yellow" />
      <span
        aria-hidden="true"
        className="absolute left-[14%] top-[33%] aspect-square w-[1.6%] rounded-full bg-brand-purple-light"
      />
      <img src={logoJogaJunto} alt="" className="mt-[5.5cqw] block w-[22cqw]" />
      <div className="relative z-[1] mb-[-11cqw] mt-[4.5cqw] flex aspect-square w-[58cqw] items-center justify-center rounded-full bg-button-solid-hover shadow-jj-selo-anel">
        {selo.arte && <img src={selo.arte} alt="" className="block aspect-square w-[49cqw] object-contain drop-shadow-lg" />}
      </div>
      <div className="flex flex-1 flex-col items-center gap-[1.4cqw] self-stretch rounded-t-[50%_11cqw] bg-brand-yellow px-[4cqw] pb-[6cqw] pt-[17cqw] text-center">
        <span className="font-display text-[length:max(11px,3cqw)] font-bold uppercase tracking-[0.06em] text-brand-blue-dark">
          {selo.categoria ? `${selo.categoria} · ${selo.ciclo}` : selo.ciclo}
        </span>
        <span className="font-display text-[length:max(16px,5.5cqw)] font-extrabold leading-[1.15] text-brand-blue-dark">
          {selo.nome}
        </span>
        <span className="text-[length:max(12px,3.2cqw)] text-content">Ciclo {selo.ciclo}</span>
        <span aria-hidden="true" className="mb-[0.5cqw] mt-[1.5cqw] h-0.5 w-[11cqw] rounded-sm bg-brand-blue" />
        <span className="font-display text-[length:max(15px,4.6cqw)] font-extrabold text-brand-blue-dark">#JogaJunto</span>
      </div>
    </div>
  );
}

/**
 * Cartão de selo, o mesmo no Meu Perfil e na galeria (AJ2-20), no layout do Meu Perfil v2: só a moldura, na proporção
 * natural, e o "Baixar" embaixo (sem "Compartilhar", por decisão do usuário). A imagem tem nome acessível com o selo.
 * "Com sua foto" mostra a moldura dos triggers; "Só o selo", a composição do design, e baixa o PNG do canvas
 * (AJ2-21, AJ2-22, AJ2-26).
 */
export default function CartaoDeSelo({ selo, temFoto, modo, onAtualizarFoto }: CartaoDeSeloProps) {
  const comFoto = molduraDoSelo(selo, temFoto);
  const rotulo = `${selo.nome}, ${selo.categoria ? `${selo.categoria}, ` : ''}ciclo ${selo.ciclo}`;
  const complemento = `do selo ${rotulo}`;

  let imagem: ReactNode;
  let acao: ReactNode;
  if (modo === 'so-o-selo') {
    imagem = <SoOSeloNoCartao selo={selo} />;
    acao = <BaixarSelo origem={() => desenharSoOSelo(selo)} arquivo={arquivoDoSoOSelo(selo)} complemento={complemento} />;
  } else if (comFoto) {
    // SPEC_DEVIATION: a moldura com foto sem quadro, raio nem fundo, e não no cartão 4/5 do design. Reason: as
    // molduras dos triggers mudam de proporção por ciclo e têm cantos e bordas recortados; o quadro as cortava.
    imagem = <img src={comFoto} alt={`Moldura do selo ${rotulo}`} className="block h-auto w-full" />;
    acao = <BaixarSelo origem={comFoto} arquivo={ARQUIVO_DO_SELO[selo.tipo]} complemento={complemento} />;
  } else {
    imagem = (
      <div className={CAIXA_DO_PEDIDO}>
        <PedidoNaMoldura temFoto={temFoto} arte={selo.arte} onAtualizarFoto={onAtualizarFoto} />
      </div>
    );
    const pedido = temFoto ? 'atualize sua foto de perfil' : 'envie uma foto de perfil';
    acao = (
      <button
        type="button"
        className={BAIXAR_SEM_MOLDURA}
        onClick={onAtualizarFoto}
        title={`Para baixar esta moldura, ${pedido}`}
      >
        <FiDownload aria-hidden="true" size={15} strokeWidth={2.4} />
        <span>
          Baixar
          <span className="sr-only">
            {' '}
            {complemento}: antes, {pedido}
          </span>
        </span>
      </button>
    );
  }

  return (
    // Na grade, os cartões de uma linha ficam da mesma altura: o "Só o selo" cresce até o maior (nome em duas
    // linhas) e o "Baixar" fica alinhado embaixo.
    <article aria-label={rotulo} className="flex h-full flex-col gap-2.5">
      {imagem}
      <div className="mt-auto flex flex-col gap-2">{acao}</div>
    </article>
  );
}
